import { ApiMatchEventIdTimelineGet200ResponseData } from "@/api/livescore/generated";

const SUPPORTED_TYPES = ["shot_on_target", "shot_off_target", "score_change"];

function calculateSingleShotXG(x: number, y: number): number {
  const goalPostY1 = 50 - 10.8 / 2; // ~44.6
  const goalPostY2 = 50 + 10.8 / 2; // ~55.4

  // Calculating range to goalposts
  const distA = Math.sqrt(Math.pow(x, 2) + Math.pow(y - goalPostY1, 2));
  const distB = Math.sqrt(Math.pow(x, 2) + Math.pow(y - goalPostY2, 2));

  // Using cosine rule to calculate angle to goal
  // angle = arccos((a^2 + b^2 - c^2) / 2ab)
  const angleRad = Math.acos(
    (distA * distA + distB * distB - 10.8 * 10.8) / (2 * distA * distB)
  );

  // Calculating distance from center of goal line
  const distance = Math.sqrt(x * x + Math.pow(y - 50, 2));

  // Simple logistic regression model: z = intercept + coeff_dist * dist + coeff_angle * angle
  // Coefficients chosen empirically for reasonable results
  const intercept = -1.5;
  const distanceCoefficient = -0.08;
  const angleCoefficient = 1.0;

  const z =
    intercept + distanceCoefficient * distance + angleCoefficient * angleRad;

  // Sigmoid function (logistic) to convert 'z' to probability (0-1)
  const xg = 1 / (1 + Math.exp(-z));

  return xg;
}

export function calculateXG(
  events: ApiMatchEventIdTimelineGet200ResponseData["match_timeline"],
  team: "home" | "away"
): number | null {
  if (!events || events.length === 0) {
    return null;
  }

  const shotEvents = events.filter(
    (event) =>
      SUPPORTED_TYPES.includes(event.type) &&
      event.competitor === team &&
      typeof event.x === "number" &&
      typeof event.y === "number"
  );

  // Calculating xG for each shot and summing the values
  const totalXG = shotEvents.reduce((acc, event) => {
    // Normalizing coordinates, so 'x' always means distance from goal line
    // Home team attacks at x=100, so distance is 100 - event.x
    // Away team attacks at x=0, so distance is simply event.x
    const normalizedX = team === "home" ? 100 - event.x! : event.x!;
    const normalizedY = event.y!;

    // Adding xG of single shot to accumulator
    return acc + calculateSingleShotXG(normalizedX, normalizedY);
  }, 0); // Starting summing from 0

  // Returning result rounded to 2 decimal places
  return parseFloat(totalXG.toFixed(2));
}
